import { useEffect, useState } from "react";
import { Users, Save, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { getConfig, updateConfig } from "@/lib/api";
import { fmtBRL, fmtPct, pct } from "@/lib/format";

export default function TeamGoalCard({ receitaAtual = 0, config, onChange, readOnly = false }) {
  const [goal, setGoal] = useState(config?.team_goal ?? 0);
  const [bonus, setBonus] = useState(config?.team_bonus_pct ?? 0.03);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setGoal(config?.team_goal ?? 0);
    setBonus(config?.team_bonus_pct ?? 0.03);
  }, [config]);

  const atingido = pct(receitaAtual, Number(goal) || 0);
  const bateu = Number(goal) > 0 && receitaAtual >= Number(goal);

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await updateConfig({
        plans: config.plans || [],
        bonus_pct: config.bonus_pct ?? 0.05,
        team_goal: Number(goal) || 0,
        team_bonus_pct: Number(bonus) || 0,
      });
      onChange && onChange(saved);
      toast.success("Meta coletiva salva");
    } catch { toast.error("Erro ao salvar meta coletiva"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="team-goal-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Meta coletiva do time</h3>
        </div>
        {bateu ? (
          <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/40 border">
            <Trophy className="w-3 h-3 mr-1" /> Meta batida
          </Badge>
        ) : (
          <Badge variant="outline">Em andamento</Badge>
        )}
      </div>

      <div className="flex items-end justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Receita atual</div>
          <div className="font-heading text-3xl font-extrabold tabular-nums font-mono">{fmtBRL(receitaAtual)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Meta</div>
          <div className="font-heading text-xl font-semibold tabular-nums font-mono">{fmtBRL(Number(goal) || 0)}</div>
        </div>
      </div>
      <Progress value={Math.min(atingido, 100)} className="h-2.5" />
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span className={`font-semibold tabular-nums ${bateu ? "text-emerald-500" : "text-amber-500"}`}>
          {fmtPct(atingido)} atingido
        </span>
        <span className="tabular-nums">Bônus coletivo: {fmtPct((Number(bonus) || 0) * 100)} sobre a comissão base</span>
      </div>

      {!readOnly && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3 items-end pt-4 border-t border-border">
          <div className="space-y-1.5">
            <Label className="text-xs">Meta coletiva mensal (R$)</Label>
            <Input type="number" min="0" step="0.01" value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="input-team-goal" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Bônus coletivo (0–1)</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" step="0.01" max="1" value={bonus} onChange={(e) => setBonus(e.target.value)} data-testid="input-team-bonus" />
              <span className="text-xs text-muted-foreground w-12 tabular-nums">{fmtPct((Number(bonus) || 0) * 100)}</span>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-team-goal">
            <Save className="w-4 h-4 mr-2" /> Salvar
          </Button>
        </div>
      )}
    </div>
  );
}
